import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CaretUp, CaretDown, EyeSlash, Eye, FloppyDisk, ArrowCounterClockwise } from "@phosphor-icons/react";
import { adminApi } from "./api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATALOG = [
  { key: "hero",       label: "Hero Banner",             desc: "Full-width sliding hero at the top of the page." },
  { key: "story",      label: "Our Story",               desc: "Scroll-pinned journey timeline with 6 milestones." },
  { key: "brands",     label: "Brands We Represent",     desc: "Grid of partner brands with links to detail pages." },
  { key: "services",   label: "What We Do",              desc: "Service pillars (System Integration, MEP, End Users)." },
  { key: "highlights", label: "Trusted Across Regions",  desc: "Customer base counts + geographic expansion cards." },
  { key: "about",      label: "About OpSys",             desc: "Editorial block with company overview and CTA." },
  { key: "industries", label: "Industries We Serve",     desc: "Industry cards grid (BFSI, Healthcare, Retail, ...)." },
  { key: "faq",        label: "FAQ",                     desc: "Accordion of frequently-asked questions." },
  { key: "clients",    label: "Trusted by Clients",      desc: "Auto-sliding client-logo marquee." },
];

const DEFAULT_CONFIG = {
  order:   CATALOG.map((c) => c.key),
  visible: Object.fromEntries(CATALOG.map((c) => [c.key, ["hero","story","brands","services","highlights"].includes(c.key)])),
};

export default function HomeSectionsManager() {
  const [order, setOrder] = useState(DEFAULT_CONFIG.order);
  const [visible, setVisible] = useState(DEFAULT_CONFIG.visible);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const map = useMemo(() => Object.fromEntries(CATALOG.map((c) => [c.key, c])), []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/site-content`)
      .then((r) => r.json())
      .then((data) => {
        const raw = data?.page?.["home.sections"];
        let cfg = null;
        if (typeof raw === "string") { try { cfg = JSON.parse(raw); } catch { cfg = null; } }
        else if (raw && typeof raw === "object") { cfg = raw; }
        if (cfg && Array.isArray(cfg.order) && cfg.visible) {
          const knownKeys = new Set(CATALOG.map((c) => c.key));
          const ord = cfg.order.filter((k) => knownKeys.has(k));
          CATALOG.forEach((c) => { if (!ord.includes(c.key)) ord.push(c.key); });
          const vis = { ...DEFAULT_CONFIG.visible, ...cfg.visible };
          setOrder(ord);
          setVisible(vis);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };

  const toggle = (key) => setVisible((v) => ({ ...v, [key]: !v[key] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { order, visible };
      await adminApi.put("/admin/page-content", {
        key: "home.sections",
        value: JSON.stringify(payload),
      });
      toast.success("Home sections saved — refresh the site to see changes.");
    } catch (e) {
      toast.error("Failed to save: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm("Reset to the default 5-section layout?")) return;
    setOrder(DEFAULT_CONFIG.order);
    setVisible({ ...DEFAULT_CONFIG.visible });
  };

  if (loading) return <div className="p-8 text-[#6B7A8D]">Loading…</div>;

  const visibleCount = order.filter((k) => visible[k]).length;

  return (
    <div className="p-6 md:p-8" data-testid="home-sections-manager">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-[26px] tracking-tight" style={{ color: "#0F2A6E" }}>
            Home Sections
          </h1>
          <p className="mt-1 text-[13px] text-[#6B7A8D] max-w-2xl">
            Choose which sections appear on the home page and in what order.
            Drag using the arrow buttons; toggle visibility with the eye icon.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#6B7A8D]">
            <span className="font-semibold text-[#0F2A6E]">{visibleCount}</span> of {order.length} visible
          </span>
          <button
            type="button"
            onClick={reset}
            data-testid="home-sections-reset"
            className="h-9 px-3 rounded-md border border-slate-200 text-[12.5px] font-medium hover:bg-slate-50 inline-flex items-center gap-1.5"
            style={{ color: "#0F2A6E" }}
          >
            <ArrowCounterClockwise size={13} weight="bold" /> Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="home-sections-save"
            className="h-9 px-4 rounded-md text-white text-[12.5px] font-semibold hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-1.5"
            style={{ background: "#1B6FE0" }}
          >
            <FloppyDisk size={13} weight="fill" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Section list */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="home-sections-list">
        {order.map((key, idx) => {
          const meta = map[key] || { label: key, desc: "" };
          const isVis = !!visible[key];
          return (
            <div
              key={key}
              data-testid={`home-section-row-${key}`}
              className={`flex items-center gap-4 px-4 md:px-6 py-4 border-b border-slate-100 last:border-b-0 transition ${
                isVis ? "bg-white" : "bg-slate-50/60"
              }`}
            >
              {/* Order buttons */}
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`home-section-up-${key}`}
                  className="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                  aria-label="Move up"
                >
                  <CaretUp size={11} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, +1)}
                  disabled={idx === order.length - 1}
                  data-testid={`home-section-down-${key}`}
                  className="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                  aria-label="Move down"
                >
                  <CaretDown size={11} weight="bold" />
                </button>
              </div>

              {/* Index chip */}
              <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-heading font-bold text-[13px]"
                   style={{ background: isVis ? "#0F2A6E" : "#E2E8F0", color: isVis ? "#FFFFFF" : "#0F2A6E" }}>
                {String(idx + 1).padStart(2, "0")}
              </div>

              {/* Meta */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-[15px]" style={{ color: "#0F2A6E" }}>
                    {meta.label}
                  </div>
                  <span className="text-[10.5px] px-1.5 py-0.5 rounded font-mono" style={{ background: "#F1F5F9", color: "#6B7A8D" }}>
                    #{key}
                  </span>
                </div>
                {meta.desc && (
                  <div className="mt-0.5 text-[12.5px] text-[#6B7A8D]">
                    {meta.desc}
                  </div>
                )}
              </div>

              {/* Visibility toggle */}
              <button
                type="button"
                onClick={() => toggle(key)}
                data-testid={`home-section-toggle-${key}`}
                aria-pressed={isVis}
                className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-[12px] font-semibold transition ${
                  isVis
                    ? "border-transparent text-white"
                    : "border-slate-200 text-[#0F2A6E] hover:bg-slate-100"
                }`}
                style={isVis ? { background: "#1B6FE0" } : {}}
              >
                {isVis ? <><Eye size={13} weight="bold" /> Visible</>
                       : <><EyeSlash size={13} weight="bold" /> Hidden</>}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[12px] text-[#6B7A8D]">
        Tip — the site fetches this config on every page load. Users will see the new layout right after you save.
      </div>
    </div>
  );
}
